import React from 'react';

import { styled } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';

import { FORM_IDS } from 'providers/LoginProvider';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import {
  hasValue,
  NumberTextField,
  PasswordTextField,
  TextField,
} from 'swipeclock-frontend';

// ########################################################
// #### Styled Components
// ########################################################

export const FormWrapper = styled(
  // eslint-disable-next-line no-unused-vars
  ({ customBackground, isEnclosedMode, ...props }) => <Box {...props} />
)(({ theme, customBackground, isEnclosedMode }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backgroundColor = hasValue(customBackground)
    ? customBackground
    : theme.palette.custom.navyBlue.main;
  return {
    display: 'grid',
    placeItems: 'center',
    alignItems: isEnclosedMode ? 'baseline' : 'center',
    width: '100%',
    minHeight: '100%',
    backgroundColor: isMobile
      ? theme.palette.primary.contrastText
      : backgroundColor,
  };
});

export const FormInputGrid = styled((props) => {
  const { noHeader, ...otherProps } = props;
  return <Card {...otherProps} />;
})(({ theme, noHeader }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const topAreas = noHeader ? `'error'` : `'header' 'error'`;
  const gridAreas = `${topAreas} 'username' 'password' 'siteId' 'buttonSet' 'footer'`;
  return {
    display: 'grid',
    gridTemplateAreas: gridAreas,
    gridTemplateColumns: '1fr',
    gap: theme.spacing(4),
    columnGap: theme.spacing(4),
    padding: theme.spacing(4),
    width: isMobile ? '100%' : '75%',
    maxWidth: '600px',
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: `0px 5px 5px -3px rgba(0,0,0,${
      isMobile ? 0.0 : 0.2
    }), 0px 8px 10px 1px rgba(0,0,0,${
      isMobile ? 0.0 : 0.14
    }), 0px 3px 14px 2px rgba(0,0,0,${isMobile ? 0.0 : 0.12})`,
    position: 'relative',
  };
});

export const ForgotPasswordClockFormInputGrid = styled(
  // eslint-disable-next-line no-unused-vars
  ({ isMobile, noHeader, ...props }) => <FormInputGrid {...props} />
)(({ isMobile, noHeader }) => {
  let topAreas = noHeader ? `'error error'` : `'header header' 'error error'`;
  let gridTemplateAreas = `
    ${topAreas}
    'text text'
    'username email'
    'buttonSet buttonSet'
    'footer footer'
  `;
  let gridTemplateColumns = '1fr 1fr';
  if (isMobile) {
    topAreas = noHeader ? `'error'` : `'header' 'error'`;
    gridTemplateAreas = `
      ${topAreas}
      'text'
      'username'
      'email'
      'buttonSet'
      'footer'
    `;
    gridTemplateColumns = '1fr';
  }
  return {
    gridTemplateAreas,
    gridTemplateColumns,
  };
});

export const ResetPasswordCodeFormInputGrid = styled((props) => {
  const { isMobile, noHeader, ...otherProps } = props;
  return <FormInputGrid {...otherProps} />;
})(({ isMobile, noHeader }) => {
  let topAreas = noHeader ? `'error error'` : `'header header' 'error error'`;
  let gridTemplateAreas = `
    ${topAreas}
    'text text'
    'resetCode resetCode' 
    'newPassword confirmPassword'
    'buttonSet buttonSet'
    'footer footer'
  `;
  let gridTemplateColumns = '1fr 1fr';
  if (isMobile) {
    topAreas = noHeader ? `'error'` : `'header' 'error'`;
    gridTemplateAreas = `
      ${topAreas}
      'text'
      'resetCode'
      'newPassword'
      'confirmPassword'
      'buttonSet'
      'footer'
    `;
    gridTemplateColumns = '1fr';
  }
  return {
    gridTemplateAreas,
    gridTemplateColumns,
  };
});

export const ResetPasswordFormInputGrid = styled(
  // eslint-disable-next-line no-unused-vars
  ({ isMobile, noHeader, ...props }) => <FormInputGrid {...props} />
)(({ isMobile, noHeader }) => {
  let topAreas = noHeader ? `'error error'` : `'header header' 'error error'`;
  let gridTemplateAreas = `
    ${topAreas}
    'newPassword confirmPassword'
    'buttonSet buttonSet'
    'footer footer'
  `;
  let gridTemplateColumns = '1fr 1fr';
  if (isMobile) {
    topAreas = noHeader ? `'error'` : `'header' 'error'`;
    gridTemplateAreas = `
      ${topAreas}
      'newPassword'
      'confirmPassword'
      'buttonSet'
      'footer'
    `;
    gridTemplateColumns = '1fr';
  }
  return {
    gridTemplateAreas,
    gridTemplateColumns,
  };
});

export const EmailField = styled((props) => (
  <TextField
    autoComplete="email"
    id={FORM_IDS.EMAIL}
    maxLength={50}
    required
    type="email"
    {...props}
  />
))(() => ({
  gridArea: 'email',
}));

export const ErrorField = styled((props) => <Alert {...props} />)(() => ({
  gridArea: 'error',
}));

export const PasswordField = styled(PasswordTextField)(() => ({
  gridArea: 'password',
}));

export const NumberField = styled(NumberTextField)(() => ({
  gridArea: 'siteId',
}));

export const AutocompleteField = styled(Autocomplete)(() => ({
  gridArea: 'siteId',
}));

export const HeaderImage = styled('img')(() => ({
  gridArea: 'header',
  height: '200px',
}));

export const HeaderTypography = styled((props) => (
  <Typography variant="h3" {...props} />
))(({ theme }) => ({
  gridArea: 'header',
  textAlign: 'center',
  color: theme.palette.primary.main,
}));

export const FooterLink = styled('a')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.primary.main,
  fontSize: theme.spacing(1.5),
  textDecoration: 'none',
  gridArea: 'footer',
}));

export const FooterText = styled('div')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.primary.main,
  fontSize: theme.spacing(1.5),
  textDecoration: 'none',
  gridArea: 'footer',
}));

export const StyledText = styled('div')(({ theme }) => ({
  textAlign: 'left',
  width: '100%',
  fontSize: theme.spacing(1.75),
  gridArea: 'text',
}));

export const UsernameField = styled(TextField)(() => ({
  gridArea: 'username',
}));

export const NewPasswordField = styled(PasswordTextField)(() => ({
  gridArea: 'newPassword',
}));

export const ConfirmPasswordField = styled(PasswordTextField)(() => ({
  gridArea: 'confirmPassword',
}));

export const ResetCodeField = styled(TextField)(() => ({
  gridArea: 'resetCode',
}));

export const ButtonSetWrapper = styled(({ isMobile, ...props }) => (
  <Stack
    display="flex"
    justifyContent="center"
    gap={1}
    direction={isMobile ? 'column-reverse' : 'row'}
    {...props}
  />
))(() => ({
  gridArea: 'buttonSet',
}));

export const ErrorWrapper = styled(Box)(() => ({
  width: '100%',
  gridArea: 'error',
}));

export const ErrorTransitionWrapper = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  '&.error-text-enter': {
    display: 'block',
    opacity: 0,
    maxHeight: '0px',
  },

  '&.error-text-enter-active': {
    opacity: 1,
    maxHeight: '24px',
    transition: `opacity ${theme.transitions.duration.shorter}ms ease-in, max-height ${theme.transitions.duration.shorter}ms ease-in`,
  },

  '&.error-text-exit': {
    opacity: 1,
    maxHeight: '24px',
  },

  '&.error-text-exit-active': {
    opacity: 0,
    maxHeight: '0px',
    transition: `opacity ${theme.transitions.duration.shorter}ms ease-out, max-height ${theme.transitions.duration.shorter}ms ease-out`,
  },
}));

export const InputErrorText = styled((props) => (
  <Typography variant="caption" {...props} />
))(({ theme }) => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  left: theme.spacing(1),
  position: 'relative',
}));
